import { useEffect, useState, useRef } from "react";
import { FileUp, Boxes, Printer, Loader2 } from "lucide-react";

const STEPS = [
  { key: "import", label: "Import PDF", icon: FileUp, caption: "Drop a shop drawing — dimensions, volume, mass and 28-day strength are read automatically." },
  { key: "pack", label: "Auto-pack the yard", icon: Boxes, caption: "One click packs panels onto the casting tables in tidy rows, grouped by concrete strength." },
  { key: "print", label: "Print the day", icon: Printer, caption: "Hand the crew a scaled schedule with every panel, table and concrete load for the shift." },
];
const DURATION = 4600;

export function DemoSection() {
  const [active, setActive] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (!paused.current) setActive((a) => (a + 1) % STEPS.length);
    }, DURATION);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="demo"
      data-testid="demo-section"
      className="max-w-7xl mx-auto px-5 py-20 md:py-28"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      <style>{`
        @keyframes ez-scan { 0%{top:0%} 100%{top:100%} }
        @keyframes ez-prog { from{width:0%} to{width:100%} }
        @keyframes ez-rowin { from{opacity:0;transform:translateX(-10px)} to{opacity:1;transform:translateX(0)} }
        @keyframes ez-pop { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
        .ez-rowin{opacity:0;animation:ez-rowin .5s ease forwards}
        @media (prefers-reduced-motion: reduce){ .ez-rowin{animation:none;opacity:1} }
      `}</style>

      <div className="max-w-2xl mb-10">
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B75DF]">// See it work</span>
        <h2 className="mt-3 font-heading font-black text-3xl sm:text-5xl tracking-tight">From PDF to pour, in three moves.</h2>
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        {/* step selector */}
        <div className="lg:col-span-4 flex flex-col gap-3">
          {STEPS.map((s, i) => {
            const on = i === active;
            return (
              <button
                key={s.key}
                data-testid={`demo-step-${i}`}
                onClick={() => setActive(i)}
                className={`relative text-left border p-5 transition-colors ${on ? "bg-[#16181A] border-[#2560C9]" : "bg-[#0E1012] border-[#2E3033] hover:border-[#2E3033]/80"}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 shrink-0 flex items-center justify-center border transition-colors ${on ? "bg-[#2560C9] border-[#2560C9] text-white" : "bg-[#2560C9]/10 border-[#2560C9]/30 text-[#3B75DF]"}`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-[#6E7787]">0{i + 1}</span>
                      <span className="font-heading font-bold text-white">{s.label}</span>
                    </div>
                  </div>
                </div>
                <p className={`mt-3 text-sm leading-relaxed ${on ? "text-[#A0AABF]" : "text-[#6E7787]"}`}>{s.caption}</p>
                {on && (
                  <div className="absolute left-0 bottom-0 h-0.5 w-full bg-[#2E3033]">
                    <div key={active} className="h-full bg-[#2560C9]" style={{ animation: `ez-prog ${DURATION}ms linear forwards` }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* screen */}
        <div className="lg:col-span-8">
          <div className="bg-[#0E1012] border border-[#2E3033] shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#2E3033]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]/70" />
              <span className="ml-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#6E7787]">EAZIKAST — {STEPS[active].label}</span>
            </div>
            <div className="relative p-5" style={{ minHeight: 340 }} key={active}>
              {active === 0 && <ImportDemo />}
              {active === 1 && <PackDemo />}
              {active === 2 && <PrintDemo />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ImportDemo() {
  const rows = [
    ["W-101", "150", "14.4", "2.16", "40"],
    ["W-102", "150", "12.0", "1.80", "40"],
    ["C-210", "200", "9.6", "1.92", "32"],
    ["B-330", "250", "18.0", "4.50", "50"],
  ];
  return (
    <div>
      <div className="relative bg-[#0A0A0A] border border-dashed border-[#2560C9]/50 h-24 flex items-center justify-center overflow-hidden">
        <div className="absolute left-0 right-0 h-6 bg-gradient-to-b from-[#2560C9]/30 to-transparent" style={{ animation: "ez-scan 1.4s ease-in-out infinite" }} />
        <div className="flex items-center gap-2 text-[#A0AABF] text-xs font-mono">
          <Loader2 className="w-4 h-4 animate-spin text-[#3B75DF]" /> parsing WALL-SET-A.pdf …
        </div>
      </div>
      <div className="mt-4 border border-[#2E3033]">
        <div className="grid grid-cols-[1fr_60px_60px_60px_56px] gap-2 px-3 py-2 border-b border-[#2E3033] text-[9px] font-mono uppercase tracking-wider text-[#6E7787]">
          <span>Mark</span><span>t (mm)</span><span>Area</span><span>Vol</span><span>MPa</span>
        </div>
        {rows.map((r, i) => (
          <div key={r[0]} className="ez-rowin grid grid-cols-[1fr_60px_60px_60px_56px] gap-2 px-3 py-2 border-b border-[#2E3033] last:border-0 text-xs font-mono text-[#A0AABF]" style={{ animationDelay: `${0.5 + i * 0.35}s` }}>
            <span className="text-white font-bold">{r[0]}</span><span>{r[1]}</span><span>{r[2]} m²</span><span>{r[3]} m³</span>
            <span className="text-[#3B75DF]">{r[4]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PackDemo() {
  const panels = [
    { id: "W-101", x: 3, y: 8, w: 40, h: 26, c: "#2560C9", d: 0 },
    { id: "W-102", x: 45, y: 8, w: 40, h: 26, c: "#3B75DF", d: 0.25 },
    { id: "C-210", x: 3, y: 38, w: 36, h: 26, c: "#F59E0B", d: 0.5 },
    { id: "C-211", x: 41, y: 38, w: 44, h: 26, c: "#2560C9", d: 0.75 },
    { id: "B-330", x: 3, y: 68, w: 48, h: 26, c: "#3B75DF", d: 1 },
    { id: "B-331", x: 53, y: 68, w: 32, h: 26, c: "#10B981", d: 1.25 },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3 text-[10px] font-mono">
        <span className="text-[#6E7787]">TABLE A · 40.0 × 9.0 m</span>
        <span className="text-[#10B981]">✓ AUTO-PACKED · GROUPED BY MPa</span>
      </div>
      <div className="relative bg-[#0A0A0A] border border-[#2E3033]" style={{ height: 280, backgroundImage: "linear-gradient(#1C2026 1px,transparent 1px),linear-gradient(90deg,#1C2026 1px,transparent 1px)", backgroundSize: "20px 20px" }}>
        {panels.map((p) => (
          <div key={p.id} className="absolute flex flex-col items-center justify-center" style={{ left: `${p.x}%`, top: `${p.y}%`, width: `${p.w}%`, height: `${p.h}%`, backgroundColor: `${p.c}dd`, border: `1px solid ${p.c}`, opacity: 0, animation: "ez-pop .5s ease forwards", animationDelay: `${p.d}s` }}>
            <span className="text-[10px] font-mono font-bold text-white leading-none">{p.id}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PrintDemo() {
  return (
    <div className="bg-white text-black p-4" style={{ animation: "ez-pop .5s ease forwards" }}>
      <div className="flex items-center justify-between border-b-2 border-black pb-2">
        <div>
          <div className="font-black text-sm tracking-tight">DAILY YARD SCHEDULE</div>
          <div className="text-[9px] font-mono text-gray-500">NORTH YARD · 2026-06-12</div>
        </div>
        <div className="text-[9px] font-mono text-gray-500">EAZIKAST</div>
      </div>
      <div className="mt-3 text-[9px] font-mono uppercase tracking-wider text-gray-500">Yard Map</div>
      <div className="relative border border-gray-300 mt-1" style={{ height: 96, backgroundImage: "linear-gradient(#eee 1px,transparent 1px),linear-gradient(90deg,#eee 1px,transparent 1px)", backgroundSize: "12px 12px" }}>
        {[{ x: 2, y: 8, w: 34, h: 34, c: "#2560C9" }, { x: 40, y: 8, w: 28, h: 34, c: "#3B75DF" }, { x: 2, y: 52, w: 44, h: 38, c: "#F59E0B" }, { x: 72, y: 8, w: 20, h: 82, c: "#10B981" }].map((b, i) => (
          <div key={i} className="absolute flex items-center justify-center" style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%`, backgroundColor: b.c }}>
            <span className="text-[7px] font-mono text-white font-bold">P-{i + 1}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[{ n: "LOAD 01", v: "6.3", ok: true }, { n: "LOAD 02", v: "4.6", ok: true }, { n: "LOAD 03", v: "6.8", ok: false }].map((l) => (
          <div key={l.n} className="border border-gray-300 p-1.5">
            <div className="text-[8px] font-mono font-bold">{l.n}</div>
            <div className="text-[8px] font-mono" style={{ color: l.ok ? "#0a7d33" : "#c00" }}>{l.v} / 6.8 m³</div>
          </div>
        ))}
      </div>
    </div>
  );
}
